import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router-dom';
import { isEmpty } from 'lodash-es';

import DefaultOrgLogo from '../../assets/images/company-logo.png';
import { locationObjToStr, renderSalaryRange } from '../../helpers/commonHelper';
import useReferralProviderData from '../../hooks/useReferralProviderData';
import { useSearchJobsQuery } from '../../redux/apis/jobsApi';
import { BaseSelect } from '../shared/BaseSelect/BaseSelect';
import ExperientialRewardsPill from '../shared/ExperientialRewardsPill/ExperientialRewardsPill';
import MonetaryRewardsPill from '../shared/MonetaryRewardsPill/MonetaryRewardsPill';

import styles from './OpeningSelect.module.scss';

export const OpeningSelect = ({
  organizationId,
  onChange,
  value,
  hasError = false,
  disabled = false,
  applicationFlow = false,
  isReferredJob = false,
  ...otherProps
}) => {
  const { t } = useTranslation('common');
  const [searchTerm, setSearchTerm] = useState('');
  const [isFocused, setIsFocused] = useState(false);
  const { referralId, applicationId } = useParams();
  const { referralProvider } = useReferralProviderData();
  // When the candidate reached this form through a "My Referral Link", surface the
  // referral provider's full referable scope (team_only included) in the opening list.
  const rp = referralProvider?.user?.slug;
  const { data: jobs, isFetching } = useSearchJobsQuery(
    { searchTerm, organizationId, applicationFlow, public_job: !!(referralId || applicationId), isReferredJob, rp },
    { skip: !organizationId }
  );

  const options = (jobs || []).map((job) => ({
    label: job.title,
    value: job.id,
    job,
  }));

  const filteredOptions = searchTerm
    ? options
        .map((option) => {
          const searchTermLower = searchTerm.toLowerCase();
          const titleMatch = option.job.title.toLowerCase().includes(searchTermLower);
          const externalIdMatch = option.job.external_job_id?.toLowerCase().includes(searchTermLower);

          if (titleMatch || externalIdMatch) {
            return {
              ...option,
              label: externalIdMatch ? String(option.job.external_job_id || '') : String(option.job.title || ''),
            };
          }
          return null;
        })
        .filter(Boolean)
    : options;

  const selectedOptionForValue = filteredOptions.find((opt) => opt.value === value);

  const formatOptionLabel = ({ job }) => {
    if (!job) return null;
    const logo = job.logo_image_url || DefaultOrgLogo;
    const org = String(job.organization_name || '');
    const location = locationObjToStr(job.location) || '';
    const salary = renderSalaryRange(job.min_salary, job.max_salary, job.currency_id) || '';
    const { monetary, currency_names, experiential } = job.rewards || {};

    // Ensure title and external_job_id are strings
    const title = String(job.title || '');
    const externalId = String(job.external_job_id || '');

    return (
      <div className={styles.optionContainer}>
        <div className={styles.optionLeft}>
          <img src={logo} alt="logo" className={styles.optionLogo} />
          <div className={styles.optionText}>
            <div className={styles.optionTitle}>
              {title} {externalId && !title.includes(externalId) ? `- ${externalId}` : ''}
            </div>
            <div className={styles.optionOrg}>{org}</div>
            <div className={styles.optionDetails}>
              {location}
              {salary && (
                <span className={styles.optionSalary}>
                  {location && '•'} {salary}
                </span>
              )}
            </div>
          </div>
        </div>
        <div className={styles.optionRight}>
          {!!monetary && <MonetaryRewardsPill monetaryReward={monetary} currencyId={currency_names} useCommas />}
          {!isEmpty(experiential) && <ExperientialRewardsPill />}
        </div>
      </div>
    );
  };

  const handleFocus = () => {
    setIsFocused(true);
    setSearchTerm('');
  };

  const handleBlur = () => {
    setIsFocused(false);
    setSearchTerm('');
  };

  const handleChange = (option) => {
    setIsFocused(false);
    setSearchTerm('');
    onChange(option);
  };

  const handleMenuClose = () => {
    setIsFocused(false);
    setSearchTerm('');
  };

  const handleMenuOpen = () => {
    setIsFocused(true);
    setSearchTerm('');
  };

  const handleInputChange = (inputValue) => {
    if (isFocused) {
      setSearchTerm(inputValue);
    }
  };

  return (
    <BaseSelect
      placeholder={t('placeholders.selectOpening')}
      className="opening-select"
      noOptionsMessage={() => (organizationId ? t('browse.noOpeningsFound') : t('browse.selectCompanyFirst'))}
      onInputChange={handleInputChange}
      onChange={handleChange}
      value={isFocused ? null : selectedOptionForValue}
      inputValue={isFocused ? searchTerm : ''}
      options={filteredOptions}
      isLoading={isFetching}
      isClearable={false}
      formatOptionLabel={formatOptionLabel}
      hasError={hasError}
      menuMaxHeight="300px"
      onFocus={handleFocus}
      onBlur={handleBlur}
      onMenuClose={handleMenuClose}
      onMenuOpen={handleMenuOpen}
      isDisabled={disabled}
      {...otherProps}
    />
  );
};
